import type { CrmContact } from '@integrations/crm-contacts';

import { ChangeDetectionStrategy, Component, HostListener, computed, input, output } from '@angular/core';

import type { FormWidgetElement } from '@sites/entity-core';
import type { LoginEduForm } from '@sites/entity-signal-form';

import { SpFormElementSettingsSignalComponent } from '@components/sp-form-element-settings-signal';
import { CommonElementsModule } from '@web/widgets/common/common-elements.module';

/** Which of the widget's two element arrays the panel is editing. */
export type LoginEduFormType = 'signUp' | 'signIn';

/**
 * Field-settings panel. Value-in/event-out over one entry of the widget signal
 * form's `signUpElements` / `signInElements` array — which array is picked by
 * the `formType` input, since login-edu keeps the two forms' fields separate.
 */
@Component({
    selector: 'web-field-element-settings',
    templateUrl: './field-element-settings.component.html',
    styleUrls: ['./field-element-settings.component.less'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [CommonElementsModule, SpFormElementSettingsSignalComponent],
})
export class FieldElementSettingsComponent {
    public readonly form = input.required<LoginEduForm>();
    public readonly index = input.required<number>();
    public readonly formType = input.required<LoginEduFormType>();
    public readonly crmContactsVariablesList = input<CrmContact[]>([]);
    public readonly isMainField = input<boolean>(false);
    /** Passed straight through: login-edu form fields take no file uploads. */
    public readonly isFileTypeDisabled = input<boolean>(true);

    public readonly saveEvent = output<void>();
    public readonly cancelEvent = output<void>();

    public readonly elements = computed<FormWidgetElement[]>(() =>
        this.formType() === 'signUp' ? this.form().signUpElements().value() : this.form().signInElements().value()
    );

    public readonly element = computed<FormWidgetElement | null>(() => this.elements()[this.index()] ?? null);

    public onElementChange(value: FormWidgetElement): void {
        if (this.formType() === 'signUp') {
            this.form().updateSignUpElement(this.index(), value);
        } else {
            this.form().updateSignInElement(this.index(), value);
        }
    }

    public onSave(): void {
        this.saveEvent.emit();
    }

    public onReset(): void {
        this.cancelEvent.emit();
    }

    @HostListener('document:keydown.enter')
    public onEnterKeyPress(): void {
        this.onSave();
    }

    @HostListener('document:keydown.escape', ['$event'])
    public onEscapeKeyPress(event: KeyboardEvent): void {
        event.stopPropagation();
        this.onReset();
    }
}
